import Head from "next/head";
import { useRouter } from "next/router";

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  /** Intrinsic size of `ogImage`. Lets crawlers lay the card out before fetching it. */
  ogImageWidth?: number | string;
  ogImageHeight?: number | string;
  ogType?: string;
  /** Overrides the default "index, follow" — e.g. "noindex, nofollow" for gated pages. */
  robots?: string;
}

/** Maps a router locale to the OpenGraph locale format (language_TERRITORY). */
const OG_LOCALES: Record<string, string> = {
  en: "en_US",
  au: "en_AU",
  pt: "pt_PT",
  br: "pt_BR",
  fr: "fr_FR",
};

const toOgLocale = (loc?: string) => OG_LOCALES[loc ?? ""] ?? "en_US";

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  canonical,
  ogImage = "/images/og-default.png",
  ogImageWidth,
  ogImageHeight,
  ogType = "website",
  robots = "index, follow",
}) => {
  const router = useRouter();
  const { locale, locales, asPath } = router;

  // Remove query parameters and hash from path
  const cleanPath = asPath.split("?")[0].split("#")[0];

  // Build canonical URL
  const baseUrl = "https://deepneuronic.com";
  const canonicalUrl = canonical || `${baseUrl}${cleanPath}`;

  // Build full OG image URL
  const fullOgImage = ogImage.startsWith("http") ? ogImage : `${baseUrl}${ogImage}`;

  return (
    <Head>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* hreflang tags for SEO */}
      {locales?.map((loc) => (
        <link
          key={loc}
          rel="alternate"
          hrefLang={loc}
          href={`${baseUrl}/${loc}${cleanPath}`}
        />
      ))}

      {/* x-default for international targeting */}
      <link
        rel="alternate"
        hrefLang="x-default"
        href={`${baseUrl}${cleanPath}`}
      />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullOgImage} />
      {ogImageWidth && <meta property="og:image:width" content={String(ogImageWidth)} />}
      {ogImageHeight && <meta property="og:image:height" content={String(ogImageHeight)} />}
      <meta property="og:locale" content={toOgLocale(locale)} />
      {locales?.filter(loc => loc !== locale).map((loc) => (
        <meta
          key={loc}
          property="og:locale:alternate"
          content={toOgLocale(loc)}
        />
      ))}

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={canonicalUrl} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={fullOgImage} />

      {/* Additional SEO */}
      <meta name="robots" content={robots} />
      <meta name="language" content={locale} />
    </Head>
  );
};

export default SEO;
